// AnalyseCreateDropzone.js
import React, { useRef } from "react";
import { Typography, Button } from "antd";
import { FilePdfOutlined, CloudUploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { VERDICTIA_MARK_SRC } from "../../../brand/verdictiaBrand";

const { Text } = Typography;

const AnalyseCreateDropzone = ({
  onFileSelected,
  disabled = false,
  accept = ".pdf",
  allowedFormats = ["pdf"],
  maxSizeMb = 50,
}) => {
  const { t } = useTranslation("review");
  const inputRef = useRef(null);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const cinematicActive = useSelector(state => !!state.ui?.cinematicUpload?.active);

  const validateAndSelect = (file) => {
    if (!file) return;
    const extension = String(file.name || "").split(".").pop()?.toLowerCase();
    const allowed = Array.isArray(allowedFormats) && allowedFormats.length
      ? allowedFormats.map((format) => String(format).replace(/^\./, "").toLowerCase())
      : ["pdf"];
    if (!extension || !allowed.includes(extension)) {
      console.warn(`Format non autorise: ${extension || "unknown"}.`);
      return;
    }
    const maxBytes = maxSizeMb * 1024 * 1024;
    if (file.size > maxBytes) {
      console.warn(`Fichier trop volumineux (max ${maxSizeMb} MB)`);
      return;
    }
    onFileSelected?.(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer?.files?.[0];
    validateAndSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) inputRef.current?.click();
  };

  const handleFileChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.target?.files?.[0];
    validateAndSelect(file);
    // Reset input pour pouvoir re-sélectionner le même fichier
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className={`create-intake ${cinematicActive ? 'create-intake--hidden' : ''}`}>
      <div className="create-intake__bg" aria-hidden="true" />

      <div className="create-intake__hero">
        <div className="create-intake__brand">
          <img src={VERDICTIA_MARK_SRC} alt="" aria-hidden="true" />
          <span>Verdictia</span>
        </div>
        <h2>{t("stage.intake_title")}</h2>
        <p>{t("stage.intake_subtitle")}</p>
        <div className="create-intake__chips">
          <span>{t("stage.intake_tag_pdf")}</span>
          <span>{t("stage.intake_tag_confidential")}</span>
          <span>{t("stage.intake_tag_workspace")}</span>
        </div>
      </div>

      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`create-dropzone ${isDragOver ? 'is-drag' : ''} ${disabled ? 'is-disabled' : ''}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          onClick={(e) => e.stopPropagation()}
        />
        <div className="create-dropzone__inner">
          <div className="create-dropzone__icon-wrap">
            {isDragOver ? (
              <CloudUploadOutlined style={{ fontSize: 28, color: 'var(--color-primary)' }} />
            ) : (
              <FilePdfOutlined style={{ fontSize: 28, color: 'var(--color-primary)' }} />
            )}
          </div>
          <div className="create-dropzone__title">
            {isDragOver ? t("stage.intake_drop_drag") : t("stage.intake_drop_idle")}
          </div>
          <Text type="secondary" className="create-dropzone__hint">{t("stage.intake_drop_hint_primary")}</Text>
          <Text type="secondary" className="create-dropzone__hint">{t("stage.intake_drop_hint_secondary", { maxSizeMb })}</Text>
          <div className="create-dropzone__cta">
            <Button type="primary" onClick={handleClick}>{t("stage.intake_browse")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyseCreateDropzone;
